import { environment } from '../../environments/environment';

/**
 * Which module owns a given backend request — the question "is this write allowed?" reduces to
 * "is the caller a Viewer in the module that owns this endpoint?".
 *
 * Most APIs serve exactly one module, but BC_Corporate serves four (Corporate, HR, Compliance and
 * Reporting), so its requests have to be attributed by path. The rules below are ordered and
 * first-match-wins; order matters in at least one place (`timesheets/reports/...` is a Corporate
 * timesheet endpoint, not a Reporting one, so timesheets is tested before reports).
 */

// UI module ids — match BC_Identity's `Modules` enum ints and ModulesService's module ids.
export const ModuleId = {
  Corporate: 1,
  Invoicing: 2,
  Compliance: 3,
  HR: 4,
  Reporting: 5,
  Tax: 6,
  Migration: 11,
  Billing: 12,
} as const;

// Ordered path rules for the Corporate API. Anything matching none of these is Corporate.
const CORPORATE_API_RULES: ReadonlyArray<{ pattern: RegExp; moduleId: number }> = [
  { pattern: /\/timesheets(\/|$|\?)/i, moduleId: ModuleId.Corporate },

  { pattern: /\/employees\/[^/]+\/leave(\/|$|\?)/i, moduleId: ModuleId.HR },
  { pattern: /\/admin\/leave-requests(\/|$|\?)/i, moduleId: ModuleId.HR },
  { pattern: /\/leaves(\/|$|\?)/i, moduleId: ModuleId.HR },
  { pattern: /\/holidays(\/|$|\?)/i, moduleId: ModuleId.HR },
  { pattern: /\/companies\/\d+\/organization(\/|$|\?)/i, moduleId: ModuleId.HR },
  { pattern: /\/organizations\/\d+\/employees\/[^/]+\/salaries(\/|$|\?)/i, moduleId: ModuleId.HR },

  { pattern: /\/compliance(\/|$|\?)/i, moduleId: ModuleId.Compliance },

  { pattern: /\/reports(\/|$|\?)/i, moduleId: ModuleId.Reporting },
  { pattern: /\/profitability(\/|$|\?)/i, moduleId: ModuleId.Reporting },
];

function pathOf(url: string): string {
  const q = url.indexOf('?');
  return (q === -1 ? url : url.slice(0, q)).replace(/\\/g, '/');
}

function startsWithAny(url: string, prefixes: (string | undefined)[]): boolean {
  return prefixes.some(p => !!p && url.startsWith(p));
}

/**
 * The module that owns this request, or null when the URL is not one of our module APIs (Graph,
 * third parties, local assets) and therefore is not ours to gate.
 */
export function resolveModuleIdForUrl(url: string): number | null {
  if (!url || url.startsWith('https://graph.microsoft.com')) return null;

  const cfg = environment.apiConfig as any;
  const path = pathOf(url);

  if (startsWithAny(url, [cfg.billingAPI?.endpoint])) return ModuleId.Billing;
  if (startsWithAny(url, [cfg.taxAPI?.endpoint])) return ModuleId.Tax;
  if (startsWithAny(url, [(environment as any).migration_api])) return ModuleId.Migration;
  if (startsWithAny(url, [(environment as any).invoicing_api, cfg.legacyInvoicingApi?.endpoint])) {
    return ModuleId.Invoicing;
  }
  if (/:\/\/(bc-inv-api|bc-inv-legacy-api)[a-z0-9.-]*\.azurewebsites\.net/i.test(url)) {
    return ModuleId.Invoicing;
  }

  const isCorporate =
    startsWithAny(url, [cfg.corporateAPI?.endpoint]) ||
    /:\/\/bc-corp-api[a-z0-9.-]*\.azurewebsites\.net/i.test(url);
  if (isCorporate) {
    return CORPORATE_API_RULES.find(r => r.pattern.test(path))?.moduleId ?? ModuleId.Corporate;
  }

  const isIdentity =
    startsWithAny(url, [cfg.IdentityAPI?.endpoint]) ||
    /:\/\/bc-id-api[a-z0-9.-]*\.azurewebsites\.net/i.test(url);
  if (isIdentity) return ModuleId.Corporate;

  return null;
}
